import { useLayoutContext } from "@context/layout";
import SubscriptionSnapp from "@modules/course/non-access/layout/SubscriptionSnapp";
import { useSubscriptionPlanCard } from "@modules/subscription/hooks/usePlanCard";
import { useSubscriptionPlans } from "@modules/subscription/hooks/useSubscriptionPlans";
import { Button } from "@shared/components";
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_VARIANT,
} from "@shared/components/button";
import useSubscriptionExperiments from "@shared/hooks/useSubscriptionExperiments";
import ArrowDownIcon from "@svg/new-design/arrow-down";
import ArrowLeft2Icon from "@svg/new-design/arrow-left2";
import { SUBS_ACTION_TYPE } from "@types";
import { stringifiedFarsiNum, toFarsiNumber } from "@utils/digits";
import SubscriptionShineIcon from "@svg/new-design/SubscriptionShineIcon";
import { FC, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { IPlanList, PlanItem } from "types/subscription";
import FeatureItem from "./FeatureItem";
import { modalActions } from "@context/modals";
import { MODAL_TYPES } from "types/modals";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { SUBSCRIPTION_FEATURES } from "./constants";

interface Props {
  className?: string;
  minimized?: boolean;
  type: keyof typeof SUBSCRIPTION_FEATURES;
  isFree?: boolean;
}

const SubscriptionBannerAdvertisement: FC<Props> = ({
  className,
  minimized,
  type,
  isFree,
}) => {
  const isMobile = useIsMobile();
  const [isSubsListOpen, setIsSubsListOpen] = useState(false);
  const { handleLogExperimentSubRecommender } = useSubscriptionExperiments();
  const { staticData, subscriptionData } = useLayoutContext();
  const { calculateMonthlyPrice } = useSubscriptionPlanCard();
  const { activePlanList } = useSubscriptionPlans();

  const formattedCountLabel = useMemo(
    () =>
      stringifiedFarsiNum(staticData?.statistics.countOfProducts ?? 0, 19_000),
    [staticData?.statistics.countOfProducts]
  );

  const minimumMonthlyPrice = useMemo(() => {
    const monthlyPrices = (activePlanList ?? [])?.map(
      (plan: PlanItem) =>
        calculateMonthlyPrice(plan.price, plan.duration_days) ?? 0
    );
    const result = Math.min(...monthlyPrices) ?? 162_000;
    return toFarsiNumber(result.toLocaleString());
  }, [activePlanList, calculateMonthlyPrice]);

  const openModalHandler = () => {
    modalActions.addModal(MODAL_TYPES.SELECT_SUBSCRIPTION_PLAN, {
      data: { planItems: activePlanList, productNumber: formattedCountLabel },
      config: { hideCloseIcon: true, draggableSheet: isMobile || false },
    });
  };

  const hasSubscription = Boolean(subscriptionData?.is_active);

  if (hasSubscription) return null;

  const texts = SUBSCRIPTION_FEATURES[type || "product"];

  const freeTitle =
    isFree && (!type || type === "product")
      ? (texts.title as { free: (count: string) => string }).free(
          formattedCountLabel
        )
      : null;

  return (
    <>
      <div
        className={twMerge(
          "w-full py-5 px-4 md:py-6 md:px-6 product-layout-subscription-banner-bg border border-fds-brand-light rounded-16",
          className
        )}
      >
        <div className="flex items-center gap-2">
          <div className="relative md:h-8 md:w-8 h-6 w-6">
            <SubscriptionShineIcon
              className="absolute inset-0 h-full w-full"
              role="img"
              aria-label="Faradars subscription"
            />
          </div>
          <p className="text-fds-primary headline-sm md:hidden block">
            {texts.title.mobile(formattedCountLabel)}
          </p>
          <p className="text-fds-primary headline-lg md:block hidden">
            {freeTitle || texts.title.desktop(formattedCountLabel)}
          </p>
        </div>

        <div className=" md:my-3 my-2 flex flex-col w-full gap-1">
          {texts.part_one.map((feature, idx) => {
            const textMessage: string | { mobile: string; normal: string } =
              typeof feature === "function"
                ? feature(formattedCountLabel)
                : feature;

            return <FeatureItem key={idx} feature={textMessage} />;
          })}
          <div
            className={twMerge(
              "grid transition-[grid-template-rows] duration-500 ease-in-out",
              isSubsListOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            )}
          >
            <div className="overflow-hidden min-h-0">
              <div className="flex flex-col w-full gap-1">
                {texts.part_two.map((feature, idx) => (
                  <FeatureItem key={idx} feature={feature} />
                ))}
              </div>
            </div>
          </div>
          {!isSubsListOpen && (
            <Button
              variant={BUTTON_VARIANT.TEXT_ACTION}
              size={BUTTON_SIZE.MEDIUM}
              color={BUTTON_COLOR.NEUTRAL}
              onClick={() => setIsSubsListOpen(true)}
              className="w-16 !px-1.5 mt-2"
              endIcon={<ArrowDownIcon className="h-5 w-5 md:w-6 md:h-6" />}
            >
              بیشتر
            </Button>
          )}
        </div>
        <div className="w-full md:my-4 my-2 border-dashed border-b border-fds-brand-light dashed" />
        <div className="w-full flex items-center justify-between flex-col md:flex-row gap-5  ">
          <div className="flex items-center w-full  fds-md:gap-5 gap-2">
            <div className="w-max">
              <p className="text-fds-primary text-nowrap title-sm md:title-md-medium">
                طرح‌های ۶ ماهه و یک‌ساله
              </p>
              <p className="md:body-sm body-xsm hidden md:block text-fds-secondary">
                از &nbsp;
                <span className="text-fds-primary title-sm md:title-md-medium">
                  {minimumMonthlyPrice}
                </span>
                &nbsp; تومان/ ماهیانه
              </p>
            </div>
            <div className="md:h-8 h-4 w-0 border-l border-fds-brand-light" />
            <p className="body-xsm md:hidden md:body-sm text-fds-secondary">
              از &nbsp;
              <span className="text-fds-primary title-sm md:title-md-medium">
                {minimumMonthlyPrice}
              </span>
              &nbsp; تومان/ ماهیانه
            </p>
            <div className="hidden  w-full md:flex">
              <SubscriptionSnapp minimized={minimized} />
            </div>
          </div>
          <Button
            endIcon={
              minimized ? (
                <ArrowLeft2Icon className="w-5 md:hidden" />
              ) : (
                <ArrowLeft2Icon className="w-4" />
              )
            }
            variant={BUTTON_VARIANT.FILL}
            size={BUTTON_SIZE.LARGE}
            color={BUTTON_COLOR.SECONDARY}
            onClick={() => {
              handleLogExperimentSubRecommender({
                action: SUBS_ACTION_TYPE.CLICK,
                position: "product-banner",
              });
              openModalHandler();
            }}
            className={twMerge(
              "subscription-button-bg text-fds-primary hover:text-fds-primary active:text-fds-secondary whitespace-nowrap w-full md:w-[158px]",
              minimized
                ? "md:w-[140px] md:min-w-[140px]"
                : "md:w-[158px] md:min-w-[158px]"
            )}
          >
            فعال‌سازی اشتراک
          </Button>
          <div className="flex w-full md:hidden ">
            <SubscriptionSnapp />
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscriptionBannerAdvertisement;
